import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getOrderById } from '../../services/orderService';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import AppImage from '../../components/AppImage';
import { CheckCircle, Package, Truck, MapPin, Phone, User, ShoppingBag } from 'lucide-react';
import LoadingSpinner from '../../components/LoadingSpinner';
import DataErrorFallback from '../../components/DataErrorFallback';

export default function OrderConfirmation() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchOrderDetails = async () => {
      if (!orderId) {
        setError('Order ID not provided');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const orderData = await getOrderById(orderId);
        setOrder(orderData);
      } catch (err) {
        console.error('Error fetching order:', err);
        setError(err?.message || 'Failed to load order details');
      } finally {
        setLoading(false);
      }
    };

    fetchOrderDetails();
  }, [orderId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center min-h-[60vh]">
          <LoadingSpinner size="large" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <DataErrorFallback
            error={error}
            onRetry={() => window.location?.reload()}
          />
        </div>
      </div>
    );
  }

  const shippingAddress = order?.shipping_address || {};
  const orderItems = order?.order_items || [];
  const totalAmount = parseFloat(order?.total_amount || 0);

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Success Banner */}
        <div className="bg-white rounded-lg shadow-sm p-8 mb-6 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
            <CheckCircle className="h-10 w-10 text-green-600" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Order Placed Successfully!
          </h1>
          <p className="text-gray-600 mb-4">
            Thank you for your purchase. Your order has been confirmed.
          </p>
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 rounded-lg">
            <Package className="h-5 w-5 text-blue-600" />
            <span className="text-sm font-medium text-blue-900">
              Order ID: <span className="font-mono">{order?.id?.slice(0, 8)}</span>
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {/* Shipping Information */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center gap-2 mb-4">
              <Truck className="h-5 w-5 text-blue-600" />
              <h2 className="text-lg font-bold text-gray-900">Shipping Information</h2>
            </div>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <User className="h-5 w-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-sm text-gray-500">Full Name</p>
                  <p className="text-sm font-medium text-gray-900">{shippingAddress?.full_name}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <MapPin className="h-5 w-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-sm text-gray-500">Address</p>
                  <p className="text-sm font-medium text-gray-900">{shippingAddress?.address}</p>
                  <p className="text-sm font-medium text-gray-900">{shippingAddress?.city}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Phone className="h-5 w-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-sm text-gray-500">Phone</p>
                  <p className="text-sm font-medium text-gray-900">{shippingAddress?.phone}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Order Summary */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center gap-2 mb-4">
              <Package className="h-5 w-5 text-blue-600" />
              <h2 className="text-lg font-bold text-gray-900">Order Summary</h2>
            </div>
            <div className="space-y-2 mb-4">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Subtotal</span>
                <span className="font-medium text-gray-900">₺{totalAmount?.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Shipping</span>
                <span className="font-medium text-green-600">Free</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Tax (KDV 20%)</span>
                <span className="font-medium text-gray-900">₺{(totalAmount * 0.2)?.toFixed(2)}</span>
              </div>
              <div className="border-t pt-2 flex justify-between">
                <span className="text-base font-bold text-gray-900">Total</span>
                <span className="text-base font-bold text-gray-900">
                  ₺{(totalAmount * 1.2)?.toFixed(2)}
                </span>
              </div>
            </div>
            <div className="pt-4 border-t">
              <p className="text-xs text-gray-500">
                <span className="font-medium">Status:</span>{' '}
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                  {order?.status}
                </span>
              </p>
              <p className="text-xs text-gray-500 mt-2">
                Estimated delivery: 3-5 business days
              </p>
            </div>
          </div>
        </div>

        {/* Order Items */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <ShoppingBag className="h-5 w-5 text-blue-600" />
            <h2 className="text-lg font-bold text-gray-900">Order Items</h2>
          </div>
          <div className="space-y-4">
            {orderItems?.map((item) => {
              const product = item?.products;
              return (
                <div key={item?.id} className="flex gap-4 pb-4 border-b last:border-0">
                  <div className="h-20 w-20 flex-shrink-0 rounded-lg overflow-hidden bg-gray-100">
                    <AppImage
                      src={product?.images?.[0]}
                      alt={`${product?.title} product image`}
                      className="h-full w-full object-cover"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900">
                      {product?.title}
                    </p>
                    <p className="text-sm text-gray-500 mt-1">
                      Quantity: {item?.quantity}
                    </p>
                    <p className="text-sm font-medium text-gray-900 mt-1">
                      ₺{parseFloat(item?.price_at_purchase)?.toFixed(2)} each
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-bold text-gray-900">
                      ₺{(parseFloat(item?.price_at_purchase) * item?.quantity)?.toFixed(2)}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Next Steps */}
        <div className="bg-blue-50 rounded-lg p-6 mb-6">
          <h3 className="text-sm font-bold text-blue-900 mb-2">What's Next?</h3>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• You'll receive an email confirmation shortly</li>
            <li>• Track your order status in your profile</li>
            <li>• We'll notify you when your order ships</li>
            <li>• Contact support if you have any questions</li>
          </ul>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4">
          <Button
            onClick={() => navigate('/official-store')}
            className="flex-1 flex items-center justify-center gap-2"
          >
            <ShoppingBag className="h-5 w-5" />
            Continue Shopping
          </Button>
          <Button
            onClick={() => navigate('/user-profile')}
            variant="outline"
            className="flex-1 flex items-center justify-center gap-2"
          >
            <Package className="h-5 w-5" />
            View My Orders
          </Button>
        </div>
      </div>
    </div>
  );
}